import { useState, useRef, useContext, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Flex, Box, useToast, useDisclosure } from '@chakra-ui/react';
import { AuthContext } from 'contexts/AuthContext';
import {
  ROLE_ADMIN,
  PROVINCE_ID_TO_CODE,
  LOB_PL_SPECIALTY,
  LOB_CL_PROPERTY,
} from 'variables/constants';
import Card from 'components/card/Card';
import { useOpenQuotes } from 'hooks/useOpenQuotes';
import { getLobFromLineOfBusiness } from './constants';
import ClientCard, { type ClientCardHandle } from './components/ClientCard';
import QuotesTable, {
  type QuotesTableFilters,
  type QuoteRowData,
} from './components/QuotesTable';
import StartQuoteModal from './components/StartQuoteModal';
import StartQuoteCard, {
  type StartQuoteSelection,
} from './components/StartQuoteCard';
import MissingFieldsModal from './components/MissingFieldsModal';
import QuoteOwnerDialog from './components/QuoteOwnerDialog';
import ActurisConfigBlocker from './components/ActurisConfigBlocker';
import {
  useActurisConfigStatus,
  useContactRfqs,
  useContactOperation,
  type DateRange,
} from './hooks';
import {
  validateCustomerForQuoting,
  type CustomerValidationError,
} from './utils/validateCustomer';
import { useCombineQuotes } from './hooks';

import type {
  APIActurisCustomerDetailData,
  APIMarketplaceQuoteData,
} from 'types/api.types';
import type {
  ActurisContactDetails,
  ContactCreateData,
} from 'api/quote-marketplace';

const contactToCustomerData = (
  contact: ActurisContactDetails,
): APIActurisCustomerDetailData => {
  const firstName = contact.first_name?.trim() || '';
  const lastName = contact.last_name?.trim() || '';
  const fullName =
    firstName && lastName
      ? `${firstName} ${lastName}`
      : contact.full_name?.trim() || `${firstName}${lastName}`.trim();

  return {
    id: 0,
    first_name: firstName,
    last_name: lastName,
    full_name: fullName,
    date_of_birth: contact.date_of_birth || '',
    email: contact.email || '',
    home_phone: contact.home_phone || '',
    mobile_phone: contact.mobile_phone || '',
    street_address: contact.street_address || '',
    city: contact.city || '',
    province: contact.province,
    postal_code: contact.postal_code || '',
    full_address: [
      contact.street_address,
      contact.city,
      contact.province ? PROVINCE_ID_TO_CODE[contact.province] : null,
      contact.postal_code,
    ]
      .filter(Boolean)
      .join(', '),
    contact_ref: contact.contact_ref,
  };
};

const DEFAULT_FILTERS: QuotesTableFilters = {
  lineOfBusiness: '',
};

const ActurisCustomerView = () => {
  const { contactRef } = useParams<{ contactRef: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === ROLE_ADMIN;

  const {
    canAccess,
    missingFields,
    message,
    isLoading: isConfigLoading,
  } = useActurisConfigStatus();
  const { setQuotes } = useOpenQuotes(contactRef || '');
  const {
    isPending: isContactOperationPending,
    error: contactOperationError,
    updateContact,
    getContactDetails,
  } = useContactOperation();
  const [contactData, setContactData] = useState<ActurisContactDetails | null>(
    null,
  );
  const [contactLoaded, setContactLoaded] = useState(false);
  const [selectedQuoteIds, setSelectedQuoteIds] = useState<number[]>([]);
  const [pendingOpen, setPendingOpen] = useState<{
    quotes: APIMarketplaceQuoteData[];
    open: () => void;
  } | null>(null);
  const [quotesFilters, setQuotesFilters] =
    useState<QuotesTableFilters>(DEFAULT_FILTERS);
  const [startQuoteSelection, setStartQuoteSelection] =
    useState<StartQuoteSelection | null>(null);

  const clientCardRef = useRef<ClientCardHandle>(null);
  const {
    isOpen: isMissingFieldsOpen,
    onOpen: onMissingFieldsOpen,
    onClose: onMissingFieldsClose,
  } = useDisclosure();
  const {
    isOpen: isOwnerDialogOpen,
    onOpen: onOwnerDialogOpen,
    onClose: onOwnerDialogClose,
  } = useDisclosure();
  const [validationErrors, setValidationErrors] = useState<
    CustomerValidationError[]
  >([]);

  const {
    combine: combineQuotes,
    uncombine: uncombineQuote,
    isCombining,
    isUncombining,
  } = useCombineQuotes({
    contactRef: contactRef || '',
    onCombineSuccess: () => {
      setSelectedQuoteIds([]);
    },
  });

  useEffect(() => {
    if (!contactRef || contactLoaded) return;

    const fetchContact = async () => {
      try {
        const data = await getContactDetails(contactRef);
        setContactData(data);
        setContactLoaded(true);
      } catch (err) {
        setContactLoaded(true);
      }
    };

    fetchContact();
  }, [contactRef, contactLoaded, getContactDetails]);

  const customerLoading = !contactLoaded || isContactOperationPending;
  const customerError = contactOperationError
    ? new Error(contactOperationError)
    : null;
  const customer = contactData ? contactToCustomerData(contactData) : undefined;

  const {
    quotes: mergedQuotes,
    error: quotesError,
    status: rfqsStatus,
    search: searchRfqs,
    activeDateRange,
  } = useContactRfqs(contactRef, { enabled: !!contactRef && canAccess });

  const quotes = useMemo((): QuoteRowData[] => {
    let filtered = mergedQuotes.filter((q) => q.source === 'matched');
    if (quotesFilters.lineOfBusiness) {
      const lob = parseInt(quotesFilters.lineOfBusiness, 10);
      filtered = filtered.filter((q) => q.line_of_business === lob);
    }

    return filtered.map(
      (q): QuoteRowData => ({
        id: q.quote_id ?? 0,
        line_of_business: q.line_of_business!,
        line_of_business_display: q.line_of_business_display,
        status: 0,
        status_display: q.rfq_status,
        carrier_name: q.carrier_name,
        premium: q.premium,
        quoted_at: q.quoted_at,
        effective_date: q.effective_date || '',
        group_id: q.group_id,
        group_quotes: null,
        acturis_rfq_ref: q.rfq_ref,
        can_edit: q.can_edit,
        broker_name: q.broker_name,
      }),
    );
  }, [mergedQuotes, quotesFilters]);

  const checkCustomerValidation = (): boolean => {
    const result = validateCustomerForQuoting(customer);
    if (!result.isValid) {
      setValidationErrors(result.errors);
      onMissingFieldsOpen();
      return false;
    }
    return true;
  };

  const openQuotes = (
    targets: APIMarketplaceQuoteData[],
    first: APIMarketplaceQuoteData | undefined,
  ) => {
    if (
      first &&
      (first.line_of_business === LOB_PL_SPECIALTY ||
        first.line_of_business === LOB_CL_PROPERTY)
    ) {
      navigate(
        `/admin/quote-marketplace/customer/${contactRef}/rfq/${first.id}`,
      );
      return;
    }
    setQuotes(
      targets.map((q) => ({
        id: q.id,
        line_of_business: q.line_of_business,
        rfq_ref: q.acturis_rfq_ref || undefined,
      })),
    );
    if (!first) return;
    const lob = getLobFromLineOfBusiness(first.line_of_business);
    navigate(
      `/admin/quote-marketplace/customer/${contactRef}/${lob}/${first.id}/policy-info`,
    );
  };

  const requestOpen = (
    targets: APIMarketplaceQuoteData[],
    first = targets[0],
  ) => {
    const open = () => openQuotes(targets, first);
    if (targets.every((q) => q.can_edit)) {
      open();
      return;
    }
    setPendingOpen({ quotes: targets, open });
    onOwnerDialogOpen();
  };

  const handleQuoteClick = (quote: APIMarketplaceQuoteData) =>
    requestOpen([quote]);

  const handleGroupQuoteClick = (
    allQuotes: APIMarketplaceQuoteData[],
    openQuote: APIMarketplaceQuoteData,
  ) => requestOpen(allQuotes, openQuote);

  const handleDateRangeSearch = (dateRange: DateRange) => {
    searchRfqs(dateRange);
  };

  const handleOpenQuotes = () =>
    requestOpen(quotes?.filter((q) => selectedQuoteIds.includes(q.id)) ?? []);

  const handleUpdateCustomer = async (
    data: Partial<APIActurisCustomerDetailData>,
  ) => {
    if (!contactRef) return;

    try {
      const updatedContact = await updateContact(
        contactRef,
        data as Partial<ContactCreateData>,
      );
      if (updatedContact) {
        setContactData(updatedContact);
      }
      toast({
        title: 'Customer updated',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to update customer';
      toast({
        title: errorMessage,
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    }
  };

  const handleStartQuoteSelect = (selection: StartQuoteSelection) => {
    if (!checkCustomerValidation()) return;
    setStartQuoteSelection(selection);
  };

  const handleEditClientFromModal = () => {
    clientCardRef.current?.startEditing();
  };

  const handleRfqCreated = (quoteId: number, rfqRef: string) => {
    setStartQuoteSelection(null);
    toast({
      title: `RFQ Ref # ${rfqRef} created`,
      status: 'success',
      duration: 5000,
      isClosable: true,
      position: 'top-right',
    });
    navigate(`/admin/quote-marketplace/customer/${contactRef}/rfq/${quoteId}`);
  };

  const handleQuoteCreated = (quoteId: number, lineOfBusiness: number) => {
    queryClient.invalidateQueries({
      queryKey: ['contact-rfqs-poll', contactRef],
    });
    setStartQuoteSelection(null);
    const lob = getLobFromLineOfBusiness(lineOfBusiness);
    setQuotes([{ id: quoteId, line_of_business: lineOfBusiness }]);
    navigate(
      `/admin/quote-marketplace/customer/${contactRef}/${lob}/${quoteId}/policy-info`,
    );
  };

  if (!isConfigLoading && !canAccess) {
    return (
      <Card p={6}>
        <ActurisConfigBlocker
          missingFields={missingFields}
          message={message}
          isAdmin={isAdmin}
          userId={user?.id}
        />
      </Card>
    );
  }

  return (
    <Box>
      <Flex
        gap={6}
        flexDirection={{ base: 'column', lg: 'row' }}
        alignItems="flex-start"
      >
        <Flex direction="column" gap={6} flex="1.5" minW={0}>
          <StartQuoteCard
            onSelect={handleStartQuoteSelect}
            isLoading={customerLoading}
          />

          <QuotesTable
            quotes={quotes}
            isFetchingRfqs={rfqsStatus === 'pending'}
            error={quotesError ? new Error(quotesError) : null}
            onQuoteClick={handleQuoteClick}
            onGroupQuoteClick={handleGroupQuoteClick}
            selectedIds={selectedQuoteIds}
            onSelectionChange={setSelectedQuoteIds}
            filters={quotesFilters}
            onFiltersChange={setQuotesFilters}
            dateRange={activeDateRange}
            onDateRangeSearch={handleDateRangeSearch}
            onOpenQuotes={handleOpenQuotes}
            onCombineQuotes={combineQuotes}
            onUncombineQuote={uncombineQuote}
            isCombining={isCombining}
            isUncombining={isUncombining}
          />
        </Flex>

        <ClientCard
          ref={clientCardRef}
          customer={customer}
          isLoading={customerLoading}
          error={customerError}
          onUpdate={handleUpdateCustomer}
          isUpdating={isContactOperationPending && contactLoaded}
        />
      </Flex>

      <StartQuoteModal
        selection={startQuoteSelection}
        contact={contactData}
        onClose={() => setStartQuoteSelection(null)}
        onSuccess={handleQuoteCreated}
        onRfqCreated={handleRfqCreated}
      />

      <QuoteOwnerDialog
        isOpen={isOwnerDialogOpen}
        quotes={pendingOpen?.quotes ?? []}
        onClose={onOwnerDialogClose}
        onConfirm={() => {
          onOwnerDialogClose();
          pendingOpen?.open();
        }}
      />
      <MissingFieldsModal
        isOpen={isMissingFieldsOpen}
        onClose={onMissingFieldsClose}
        errors={validationErrors}
        onEditClient={handleEditClientFromModal}
      />
    </Box>
  );
};

export default ActurisCustomerView;
